import { Injectable, ViewContainerRef } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { TabToTabChannelName } from '../../shared/enums/tab-to-tab-channel-name.enum';
import { ValidatedResponse } from '../../shared/models/validated-response.model';
import { BusinessTransactionService } from '../../shared/services/business-transaction/business-transaction.service';
import { RedirectTransactionService } from '../../shared/services/business-transaction/redirect-transaction.service';
import { TabToTabService } from '../../shared/services/tab-to-tab-messaging/tab-to-tab.service';
import { BusinessTransactionCheckin } from '../models/business-transaction-checkin.model';
import { BusinessTransactionValidationRequest } from '../models/business-transaction-validation-request';
import { BusinessUnitSourceSystemTypeMap } from '../models/business-unit-source-system-type-map';
import { NewBusinessTransaction } from '../models/new-business-transaction.model';

@Injectable()
export class CheckinService extends BaseApiService {
    public _viewContainerRef: ViewContainerRef;

    constructor(public _businessTransactionService: BusinessTransactionService, public _redirectTransactionService: RedirectTransactionService, private tabToTabService: TabToTabService) {
        super();
    }

    private sendTabToTabMessage(channelName: TabToTabChannelName, message: any): void {
        try {
            this.tabToTabService.sendMessage(channelName, message);
        } catch (error) {
            console.error(error);
        }
    }

    public checkin(businessTransactionIds: number[], agentId: number, newBusinessTransactions: NewBusinessTransaction[]): Observable<BusinessTransactionCheckin> {
        const data = { BusinessTransactionIds: businessTransactionIds, AgentId: agentId, BusinessTransactionNewServiceDetails: newBusinessTransactions };
        return this.post('Foundation/BusinessTransaction/CheckIn', data).pipe(tap(_ => {
            try{
                this.tabToTabService.sendMessage(TabToTabChannelName.ActiveBusinessTransaction, {});
            } catch (error) {
                console.error(error);
            }
        }));
    }

    public checkinAssignRedirect(businessTransactionAreaId: number, businessTransactionTypeId: number, businessTransactionParamId: number, customerNumberId: number, agentId: number, action: (number) => Observable<any> = null) {
        const self = this;
        let businessTransactionNumberId: string;
        const businessTranasction = new NewBusinessTransaction();
        businessTranasction.businessTransactionAreaId = businessTransactionAreaId;
        businessTranasction.businessTransactionTypeId = businessTransactionTypeId;
        businessTranasction.businessTransactionParamId = businessTransactionParamId;
        const newBusinessTransactions = new Array<NewBusinessTransaction>();
        newBusinessTransactions.push(businessTranasction);

        businessTranasction.customerNumberId = customerNumberId;
        businessTranasction.agentId = agentId;

        this.checkin(null, businessTranasction.customerNumberId, newBusinessTransactions)
            .subscribe(r => {
                businessTransactionNumberId = r.businessTransactionIds[0].toString();
                self._businessTransactionService.assignBusinessTransactionsToUser(businessTransactionNumberId, 0, true).subscribe(r2 => {
                    // call an action prior to navigating
                    if (action != null) {
                        action(businessTransactionNumberId).subscribe(
                            r => {
                                if (r) {
                                    self._redirectTransactionService.redirectToTransaction(businessTransactionNumberId.toString(), this._viewContainerRef);
                                }
                            },
                            e => {
                                console.log(e);
                            });
                    } else {
                        self._redirectTransactionService.redirectToTransaction(businessTransactionNumberId.toString(), this._viewContainerRef);
                    }

                }, err => console.log(err));
            }, err => console.log(err));
    }

    public getServiceAreas() {
        return this.get('Foundation/BusinessTransaction/Area');
    }

    public getBusinessTransactionTypeParamList(businessTransactionAreaId, businessTransactionTypeId) {
        return this.get('Foundation/BusinessTransaction/Area/' + businessTransactionAreaId + '/RequiredParams/' + businessTransactionTypeId);
    }

    public beginServices(businessTransactionIds: number[], agentId: number, newBusinessTransactions: NewBusinessTransaction[]) {
        const data = { BusinessTransactionIds: businessTransactionIds, AgentId: agentId, BusinessTransactionNewServiceDetails: newBusinessTransactions };
        return this.post('Foundation/BusinessTransaction/BeginServices', data);
    }

    public validateServices(businessTransactionValidationRequest: BusinessTransactionValidationRequest) {
        return this.post('Foundation/BusinessTransaction/Validate', businessTransactionValidationRequest);
    }

    public validatePrequal(businessTransactionValidationRequest: BusinessTransactionValidationRequest): Observable<BusinessTransactionValidationRequest> {
        return this.post('Foundation/BusinessTransaction/Prequal', businessTransactionValidationRequest);
    }

    public getRequiredSearchCriteria(id: number): Observable<ValidatedResponse<number[]>> {
        return this.get('Foundation/BusinessTransaction/Requirement/' + id);
    }

    public getServicesList(): Observable<ValidatedResponse<any>> {
        return this.get('Foundation/BusinessTransaction/Lookups/');
    }

    public startServices(businessTransactionIds: number[], agentId: number, newBusinessTransactions: NewBusinessTransaction[]) {
        const data = { BusinessTransactionIds: businessTransactionIds, AgentId: agentId, BusinessTransactionNewServiceDetails: newBusinessTransactions };
        return this.post('Foundation/BusinessTransaction/StartServices', data)
            .pipe(tap(_ => this.sendTabToTabMessage(TabToTabChannelName.ActiveBusinessTransaction, {})));
    }

    public searchDuplicateService(businessTransactionValidationRequest: BusinessTransactionValidationRequest) {
        return this.post('Foundation/BusinessTransaction/Services/SearchDuplicate', businessTransactionValidationRequest);
    }

    public getCheckInLocationDetails(businessUnitId, businessTransactionTypeId): Observable<BusinessUnitSourceSystemTypeMap> {
        return this.get('Foundation/BusinessTransaction/CheckinLocation/BusinessUnit/' + businessUnitId + '/BusinessTransaction/' + businessTransactionTypeId);
    }

    public saveCheckInLocationDetails(locationDetails: any) {
        return this.post('Foundation/BusinessTransaction/CheckinLocation', locationDetails);
    }

    public getCheckInConfigurations(businessUnitId): Observable<any> {
        return this.get('Foundation/BusinessTransaction/CheckinLocation/Configuration/' + businessUnitId);
    }

    public getRecentDriverPhotoExists(customerNumberId: number) {
        return this.get(`Customer/Image/DriverPhoto/Recent/Exists/${customerNumberId}`);
    }

    public getServiceCheckinDetails(businessTransactionValidationRequest: BusinessTransactionValidationRequest) {
        return this.post('Foundation/BusinessTransaction/Services/CheckInDetails', businessTransactionValidationRequest);
    }

    public transferTransaction(businessTransactionIds: number[], businessUnitId: number): Observable<BusinessTransactionCheckin> {
        const data = { BusinessTransactionIds: businessTransactionIds, BusinessTransactionNewServiceDetails: [] };
        return this.post(`Foundation/BusinessTransaction/CheckIn/Transfer/${businessUnitId}`, data);
    }
}
